import { Box, Text, Flex, Loader, Badge, Tooltip, SimpleGrid, Button } from "@mantine/core"
import { IconRefresh } from "@tabler/icons-react"
import { LEAGUE } from "../../models/NBA"
import { useEffect, useState, useRef, useCallback } from "react"
import { getLeagueLeaders } from "../../api/Info"
import { LeagueLeader, Season } from "../../client"
import classes from "./LeagueLeaders.module.css"
import { createSearchParams, useNavigate } from "react-router-dom"
import PlayerAvatar from "../Common/PlayerAvatar"

export type SimpleStat = "PTS" | "AST" | "REB" | "STL" | "BLK"

export const statNameMap: Record<SimpleStat, string> = {
  PTS: "Points",
  AST: "Assists",
  REB: "Rebounds",
  STL: "Steals",
  BLK: "Blocks",
}

interface LeagueLeadersProps {
  league: LEAGUE
  stat: SimpleStat
  season: Season
  refreshKey?: number
}

const LeagueLeaders: React.FC<LeagueLeadersProps> = ({ league, stat, season, refreshKey = 0 }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [leaders, setLeaders] = useState<LeagueLeader[]>([])
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const cacheRef = useRef<Map<string, LeagueLeader[]>>(new Map())

  const fetchData = useCallback(async (skipCache = false, signal?: AbortSignal) => {
    const cacheKey = `${league}_${season}_${stat}`
    if (!skipCache && cacheRef.current.has(cacheKey)) {
      setLeaders(cacheRef.current.get(cacheKey)!)
      setLoading(false)
      setError(false)
      return
    }

    setLoading(true)
    setError(false)

    try {
      const res = await getLeagueLeaders(league, [stat], season, signal)
      if (signal?.aborted) return
      const data = res[stat] || []
      cacheRef.current.set(cacheKey, data)
      setLeaders(data)
    } catch (err: unknown) {
      if ((err as Error)?.name === "AbortError" || signal?.aborted) return
      setError(true)
    } finally {
      if (!signal?.aborted) {
        setLoading(false)
      }
    }
  }, [league, stat, season])

  useEffect(() => {
    const controller = new AbortController()
    fetchData(refreshKey > 0, controller.signal)
    return () => controller.abort()
  }, [fetchData, refreshKey])

  const search = (player: LeagueLeader) => {
    const searchParams = `/search?${createSearchParams({
      type: "player",
      league,
      player: player.id,
      lastN: "0",
      stats: [player.stat],
      seasons: season,
      seasonType: "Regular Season",
      opponent: "0",
    })}`

    navigate(searchParams)
  }

  return (
    <Box p="md" mih={140} bg="#1f1f1f" style={{ borderRadius: 16, border: '1px solid #2a2a2a' }}>
      { loading ? (
        <Flex justify="center" align="center" mih={180}>
          <Loader size="md" />
        </Flex>
      ) : error ? (
        <Flex direction="column" align="center" justify="center" py={28} gap="xs">
          <Text c="dimmed" fz="sm">Unable to load {statNameMap[stat].toLowerCase()} leaders.</Text>
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            radius="xl"
            onClick={() => fetchData(true)}
            leftSection={<IconRefresh size={14} />}
          >
            Retry
          </Button>
        </Flex>
      ) : leaders.length === 0 ? (
        <Flex direction="column" align="center" justify="center" py={28}>
          <Text c="dimmed" fz="sm">No leader data available for {season} {statNameMap[stat].toLowerCase()}.</Text>
        </Flex>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 5 }} spacing="sm">
          { leaders.map((player, i) => {
            const isHovered = hoverIndex === i;
            const accentBorder = league === LEAGUE.WNBA ? 'rgba(255, 85, 0, 0.65)' : 'rgba(29, 155, 240, 0.65)';
            const accentGlow = league === LEAGUE.WNBA ? '0 4px 20px rgba(255, 85, 0, 0.22)' : '0 4px 20px rgba(29, 155, 240, 0.22)';
            const hoverBg = league === LEAGUE.WNBA ? '#2a2420' : '#20262e';

            return (
              <Tooltip
                key={player.id || i}
                events={{ hover: true, focus: true, touch: true }}
                label={`Click to search for ${player.name} clips`}
              >
                <Box
                  p="md"
                  className={classes.dropItem}
                  style={{
                    borderRadius: 12,
                    border: `1px solid ${isHovered ? accentBorder : '#333333'}`,
                    backgroundColor: isHovered ? hoverBg : '#252525',
                    boxShadow: isHovered ? accentGlow : 'none',
                    transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                    cursor: "pointer",
                    transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
                    animationDelay: `${i * 0.06}s`,
                  }}
                  onClick={() => search(player)}
                  onMouseEnter={() => setHoverIndex(i)}
                  onMouseLeave={() => setHoverIndex(null)}
                >
                  <Flex justify="space-between" align="center" mb="xs">
                    <Badge
                      variant="filled"
                      color={i === 0 ? "accent" : "dark"}
                      size="sm"
                      radius="xl"
                      styles={{ root: { fontWeight: 700 } }}
                    >
                      #{player.rank}
                    </Badge>
                    <Text fz="18px" fw={800} c="accent">
                      {player.value}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="sm">
                    <PlayerAvatar
                      playerId={player.id}
                      playerName={player.name}
                      league={league}
                      size={36}
                      radius="xl"
                    />
                    <Box style={{ overflow: "hidden" }}>
                      <Text fw={600} fz="sm" truncate>
                        {player.name}
                      </Text>
                      <Text fw={400} fz="xs" c="dimmed" truncate>
                        {player.teamName}
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </Tooltip>
            );
          })}
        </SimpleGrid>
      )}
    </Box>
  )
}

export default LeagueLeaders